import React, { useRef, useState, useEffect } from 'react'
import { Upload, Loader2, FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ShadcnButton } from '../ui'
import { apiService } from '../../services/api'

export interface EntityDocument {
  id: string
  name: string
  url: string
  type?: string
  expiryDate?: string
  createdAt: string
}

interface DocumentUploadProps {
  entityType: 'customer' | 'payment' | 'sale' | 'vehicle'
  entityId: string
  saleId?: string | null
  documentType?: string
  documentName?: string
  expiryDate?: string
  onUploadSuccess?: (document: EntityDocument) => void
  onUploadError?: (error: string) => void
  accept?: string
  className?: string
  label?: string
}

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
  entityType,
  entityId,
  saleId = null,
  documentType,
  documentName,
  expiryDate,
  onUploadSuccess,
  onUploadError,
  accept = '.pdf,.jpg,.jpeg,.png',
  className = '',
  label,
}) => {
  const { t } = useTranslation()
  const tp = (key: string) =>
    t(`financialSales.saleDocumentation.documentUpload.${key}`)
  const displayLabel = label ?? tp('defaultLabel')

  const inputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  const resetSelection = () => {
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return null
    })
    setPendingFile(null)
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const handleFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return URL.createObjectURL(file)
    })
    setPendingFile(file)
  }

  const performUpload = async (file: File) => {
    setIsUploading(true)
    setUploadProgress(0)

    try {
      const formData = new FormData()
      formData.append('docs', file)

      const uploadResponse = await apiService.uploadEntityDocumentTemp(formData) as Record<string, unknown>

      const documents = uploadResponse?.documents as Array<Record<string, unknown>> | undefined
      if (!documents || !Array.isArray(documents) || documents.length === 0) {
        throw new Error(tp('tempFailed'))
      }

      const raw = documents[0] as Record<string, unknown>
      const tempKey =
        (typeof raw.filename === 'string' && raw.filename) ||
        (typeof raw.fileName === 'string' && raw.fileName) ||
        ''
      const tempUrl = typeof raw.url === 'string' ? raw.url : ''

      setUploadProgress(50)

      const pathSaleId = entityType === 'sale' ? entityId : saleId ?? undefined
      const payload: {
        entityType: string
        entityId: string
        name: string
        type?: string
        expiryDate?: string
        tempFilename?: string
        saleId?: string
        url?: string
      } = {
        entityType,
        entityId,
        name: documentName || file.name,
        type: documentType,
        expiryDate,
      }

      if (tempKey) {
        payload.tempFilename = tempKey
        if (pathSaleId) {
          payload.saleId = pathSaleId
        } else if (entityType !== 'sale') {
          throw new Error(tp('saleIdRequired'))
        }
      } else if (tempUrl) {
        payload.url = tempUrl
      } else {
        throw new Error(tp('invalidTempResponse'))
      }

      const document = await apiService.createEntityDocument(payload) as EntityDocument
      setUploadProgress(100)

      resetSelection()

      if (onUploadSuccess) {
        onUploadSuccess(document)
      }
    } catch (error: unknown) {
      console.error('Erro ao fazer upload do documento:', error)
      const msg = error instanceof Error ? error.message : tp('tempFailed')
      if (onUploadError) {
        onUploadError(msg)
      }
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
    }
  }

  const handleConfirmUpload = () => {
    if (!pendingFile) return
    void performUpload(pendingFile)
  }

  const isPdf =
    pendingFile?.type === 'application/pdf' ||
    pendingFile?.name.toLowerCase().endsWith('.pdf')
  const isImage = pendingFile?.type.startsWith('image/') ?? false

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-foreground mb-2">
        {displayLabel}
      </label>

      <div className="space-y-2">
        <input
          ref={inputRef}
          type="file"
          onChange={handleFileChosen}
          accept={accept}
          className="hidden"
          disabled={isUploading}
        />

        {!pendingFile ? (
          <ShadcnButton
            type="button"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={isUploading}
            className="w-full"
          >
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {tp('uploading')}
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                {tp('selectFile')}
              </>
            )}
          </ShadcnButton>
        ) : (
          <>
            <div className="rounded-md border border-border bg-muted/30 p-3 space-y-3">
              <p className="text-sm font-medium text-foreground truncate">
                {pendingFile.name}
              </p>
              {previewUrl && isImage && (
                <img
                  src={previewUrl}
                  alt=""
                  className="max-h-48 w-full object-contain rounded border border-border"
                />
              )}
              {previewUrl && isPdf && (
                <iframe
                  src={previewUrl}
                  title={tp('previewPdf')}
                  className="h-56 w-full rounded border border-border bg-background"
                />
              )}
              {previewUrl && !isImage && !isPdf && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <FileText className="h-8 w-8 shrink-0" />
                  <span>{tp('previewOther')}</span>
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <ShadcnButton
                type="button"
                onClick={handleConfirmUpload}
                disabled={isUploading}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {tp('uploading')}
                  </>
                ) : (
                  tp('confirmUpload')
                )}
              </ShadcnButton>
              <ShadcnButton
                type="button"
                variant="outline"
                onClick={() => inputRef.current?.click()}
                disabled={isUploading}
              >
                {tp('changeFile')}
              </ShadcnButton>
              <ShadcnButton
                type="button"
                variant="ghost"
                onClick={resetSelection}
                disabled={isUploading}
              >
                {tp('cancelSelection')}
              </ShadcnButton>
            </div>
          </>
        )}

        {isUploading && uploadProgress > 0 && (
          <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-muted">
            <div
              className="bg-accent h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        )}
      </div>
    </div>
  )
}
